import { logger } from "@/lib/logger";
import {
  dismissToast,
  showProgressToast,
  showWarningToast,
  updateProgressToast,
} from "@/components/custom-toasts/toasts";
import { preloadDocumentImagesForPrint } from "./printImagePreload";

function sanitizeCss(css: string = ""): string {
  if (!css) return "";
  let out = css;
  out = out.replace(/@import[^;]*;?/gi, "");
  out = out.replace(/behavior\s*:\s*[^;]+;?/gi, "");
  out = out.replace(/expression\s*\([^\)]*\)/gi, "");
  out = out.replace(/url\s*\(\s*['\"]?([^'"\)]+)['\"]?\s*\)/gi, (m, url) => {
    const u = String(url).trim();
    if (/^(data:|blob:|\.|\/)\S*/i.test(u)) return m;
    return "";
  });
  return out;
}

// --- Simple CSS scoping: prefix selectors with .doc-root ---
//  * Handles top-level rules and @media blocks.
//  * Leaves @keyframes/@font-face untouched.
function scopeCss(css: string, scope = ".doc-root"): string {
  if (!css) return css;

  // Preserve keyframes and font-face untouched
  const preserved: Array<{ token: string; content: string }> = [];
  let idx = 0;
  const placeHolder = (label: string) => `/*__KEEP_${label}_${idx++}__*/`;

  const preserve = (re: RegExp, label: string) => {
    css = css.replace(re, (m) => {
      const ph = placeHolder(label);
      preserved.push({ token: ph, content: m });
      return ph;
    });
  };

  // Preservations
  preserve(/@keyframes[\s\S]*?\{[\s\S]*?\}[\s\S]*?\}/gi, "KEYFRAMES");
  preserve(/@font-face\s*\{[\s\S]*?\}/gi, "FONTFACE");

  // Scope @media content recursively
  css = css.replace(/@media[^\{]*\{([\s\S]*?)\}/gi, (m, inner) => {
    const scoped = scopeCss(inner, scope);
    return m.replace(inner, scoped);
  });

  // Scope top-level selector blocks: ( ... ){ ... }
  css = css.replace(/(^|})\s*([^@}{]+)\s*\{/g, (_m, prefix, selGroup) => {
    const scoped = selGroup
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean)
      .map((s: string) => {
        // Skip already scoped or globally special selectors
        if (s.startsWith(scope)) return s;
        if (s.startsWith("@")) return s;
        if (s === ":root") return `${scope} ${s}`;
        if (s.startsWith("html") || s.startsWith("body"))
          return `${scope} ${s}`;
        return `${scope} ${s}`;
      })
      .join(", ");
    return `${prefix} ${scoped} {`;
  });

  // Restore preserved blocks
  for (const p of preserved) css = css.replace(p.token, p.content);
  return css;
}

/**
 * Opens the browser print dialog for the current page.
 *
 * Layout is entirely the print stylesheets' job (`PrintStyles`, plus
 * `WorkspaceDocumentStyles` on the workspace viewer): between them they reset the
 * app shell, the on-screen zoom (`zoom: 1`, `transform: none`, and `width: 100%`
 * over the `--doc-zoom-width` the workspace doc-root renders at on screen) and
 * the `no-print` chrome for the duration of printing, and the browser reverts all
 * of it on its own afterwards. Nothing here should mutate the live DOM for
 * layout — inline overrides applied around a blocking `window.print()` need a
 * restore path that no event fires reliably, and an inline `!important` would
 * outrank the print stylesheets anyway (LAR-396).
 *
 * Content, though, has to be *there* before the snapshot. Workspace page rasters
 * are lazy-loaded (LAR-252) and `window.print()` does not wait for the fetches it
 * triggers, so the rasters are loaded up front here — see `printImagePreload`.
 * Those attribute changes need no restore path, which is what makes them safe.
 *
 * @param title - Used as the document title while printing, which browsers offer
 *   as the default filename when saving as PDF.
 */
/**
 * The `.doc-root` whose rasters are currently being fetched, if any. Held as the
 * element rather than a boolean so that a print click on a *different* document
 * is not swallowed while an abandoned preload runs itself out.
 */
let docRootBeingPrepared: Element | null = null;

const handlePrintDocumentOnly = async (title?: string) => {
  const docRoot = document.querySelector(".doc-root");

  // The preload is slow enough to double-click through; a second dialog for the
  // same document is never what the reader wanted.
  if (docRootBeingPrepared && docRootBeingPrepared === docRoot) return;
  docRootBeingPrepared = docRoot;

  const originalTitle = document.title;
  let titleWasChanged = false;
  let progressToastId: string | number | undefined;

  try {
    const preload = await preloadDocumentImagesForPrint(docRoot, {
      onStart: (pending) => {
        progressToastId = showProgressToast(
          "Henter dokumentets sider, så de kommer med i udskriften...",
          "Forbereder udskrift",
          { current: 0, total: pending }
        );
      },
      onProgress: (settled, pending) => {
        if (progressToastId === undefined) return;
        updateProgressToast(
          progressToastId,
          "Henter dokumentets sider, så de kommer med i udskriften...",
          "Forbereder udskrift",
          { current: settled, total: pending }
        );
      },
    });

    if (progressToastId !== undefined) {
      dismissToast(progressToastId);
      progressToastId = undefined;
    }

    // Fetching the rasters takes seconds, and the reader can navigate inside
    // that window. Opening the dialog then would print whatever document is now
    // on screen, under the previous one's filename.
    if (docRoot && !docRoot.isConnected) {
      logger.info("Print abandoned: the document was closed during preload", {
        operation: "handlePrintDocumentOnly",
        totalPages: preload.total,
      });
      return;
    }

    if (title) {
      document.title = title;
      titleWasChanged = true;
    }
    window.print();

    // Warn only once the dialog is gone. window.print() blocks, and the toaster
    // is display:none under @media print, so a warning raised before it would
    // have spent its whole lifetime hidden behind the dialog. Print anyway
    // rather than refusing: a partial print is still useful, as long as the
    // reader is told which pages will be blank.
    const missing = preload.failed + (preload.timedOut ? 1 : 0);
    if (missing > 0) {
      logger.warn("Some document pages could not be loaded before printing", {
        operation: "handlePrintDocumentOnly",
        totalPages: preload.total,
        failed: preload.failed,
        timedOut: preload.timedOut,
      });
      showWarningToast(
        preload.timedOut
          ? "Nogle sider blev ikke hentet i tid og vil være tomme i udskriften. Prøv igen."
          : `${preload.failed} side(r) kunne ikke hentes og vil være tomme i udskriften.`,
        "Ufuldstændig udskrift"
      );
    }
  } catch (e) {
    logger.error("Print failed", {
      operation: "handlePrintDocumentOnly",
      error: e,
    });
    if (progressToastId !== undefined) dismissToast(progressToastId);
  } finally {
    if (docRootBeingPrepared === docRoot) docRootBeingPrepared = null;
    // Only undo a title we actually set. `originalTitle` is a snapshot from
    // before the preload, and AppRouter retitles the tab per route — writing it
    // back after an abandoned print would overwrite the title the route the
    // reader navigated to had just set, and it would stay wrong until the next
    // navigation. Delayed so the print job has picked the name up first.
    if (titleWasChanged) {
      setTimeout(() => {
        document.title = originalTitle;
      }, 100);
    }
  }
};

export { sanitizeCss, scopeCss, handlePrintDocumentOnly };
